import {
  collection,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
} from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { useEffect, useMemo, useState } from "react";
import { db, functions } from "../../firebase";

type StaffInviteRole = "teacher" | "office" | "admin";

type TeacherDoc = {
  teacherKey: string;
  firstName: string;
  lastName: string;
  email: string;
  isActive: boolean;
  staffRole?: StaffInviteRole;
  gradeLevels?: string[];
  specialAssignments?: string[];
  createdAt?: any;
  updatedAt?: any;
  uid?: string;
  linkedEmail?: string;
  linkedAt?: any;
};

type Row = { id: string } & TeacherDoc;

type RosterOptionItem = {
  value: string;
  label: string;
};

type RosterOptions = {
  gradeLevels: RosterOptionItem[];
  specialPrograms: RosterOptionItem[];
};

const DEFAULT_ROSTER_OPTIONS: RosterOptions = {
  gradeLevels: [
    { value: "PK", label: "Pre-K" },
    { value: "K", label: "Kindergarten" },
    { value: "1", label: "1st Grade" },
    { value: "2", label: "2nd Grade" },
    { value: "3", label: "3rd Grade" },
    { value: "4", label: "4th Grade" },
    { value: "5", label: "5th Grade" },
    { value: "6", label: "6th Grade" },
  ],
  specialPrograms: [
    { value: "art", label: "Art" },
    { value: "music", label: "Music" },
    { value: "band", label: "Band" },
    { value: "pe", label: "PE" },
    { value: "principal", label: "Principal" },
  ],
};

function norm(s: string) {
  return s.trim();
}

function normEmail(s: string) {
  return s.trim().toLowerCase();
}

function fullName(r: { firstName?: string; lastName?: string }) {
  return `${r.firstName ?? ""} ${r.lastName ?? ""}`.trim();
}

function normalizeStaffRole(v: any): StaffInviteRole {
  const x = String(v ?? "").trim();
  if (x === "office" || x === "admin") return x;
  return "teacher";
}

function asStringArray(v: any): string[] {
  if (!Array.isArray(v)) return [];
  return Array.from(new Set(v.map((x) => String(x || "").trim()).filter(Boolean)));
}

function normalizeKey(v: string) {
  return v.trim();
}

function normalizeLabel(v: string) {
  return v.trim();
}

function sanitizeRosterItems(items: RosterOptionItem[]) {
  const seen = new Set<string>();
  const out: RosterOptionItem[] = [];
  for (const item of items) {
    const value = normalizeKey(item.value);
    const label = normalizeLabel(item.label) || value;
    if (!value) continue;
    const dedupe = value.toLowerCase();
    if (seen.has(dedupe)) continue;
    seen.add(dedupe);
    out.push({ value, label });
  }
  return out;
}

function readRosterItems(src: any, fallback: RosterOptionItem[]) {
  if (!Array.isArray(src)) return fallback;

  const mapped = src.map((item: any) => {
    if (typeof item === "string") {
      const value = normalizeKey(item);
      return { value, label: value };
    }
    const value = normalizeKey(String(item?.value ?? ""));
    const label = normalizeLabel(String(item?.label ?? "")) || value;
    return { value, label };
  });

  const clean = sanitizeRosterItems(mapped);
  return clean.length ? clean : fallback;
}

function ensurePrincipalSpecialProgram(items: RosterOptionItem[]) {
  return items.some((item) => item.value.trim().toLowerCase() === "principal")
    ? items
    : [...items, { value: "principal", label: "Principal" }];
}

function toggleListValue(list: string[], value: string) {
  return list.includes(value) ? list.filter((x) => x !== value) : [...list, value];
}

export function TeachersPage() {
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  const [teacherKey, setTeacherKey] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [inviteRole, setInviteRole] = useState<StaffInviteRole>("teacher");
  const [gradeLevels, setGradeLevels] = useState<string[]>([]);
  const [specialAssignments, setSpecialAssignments] = useState<string[]>([]);
  const [sendInviteNow, setSendInviteNow] = useState(true);
  const [saving, setSaving] = useState(false);

  const [filter, setFilter] = useState("");
  const [showInactive, setShowInactive] = useState(false);
  const [busyRowId, setBusyRowId] = useState<string | null>(null);
  const [rosterOptions, setRosterOptions] = useState<RosterOptions>(DEFAULT_ROSTER_OPTIONS);
  const [editingRowId, setEditingRowId] = useState<string | null>(null);
  const [editingGrades, setEditingGrades] = useState<string[]>([]);
  const [editingSpecials, setEditingSpecials] = useState<string[]>([]);

  const gradeLabelByValue = useMemo(() => {
    const map: Record<string, string> = {};
    rosterOptions.gradeLevels.forEach((g) => {
      map[g.value] = g.label;
    });
    return map;
  }, [rosterOptions]);

  const specialLabelByValue = useMemo(() => {
    const map: Record<string, string> = {};
    rosterOptions.specialPrograms.forEach((p) => {
      map[p.value] = p.label;
    });
    return map;
  }, [rosterOptions]);

  const filtered = useMemo(() => {
    const f = filter.trim().toLowerCase();
    return rows.filter((r) => {
      if (!showInactive && r.isActive === false) return false;
      if (!f) return true;

      const hay = `${r.teacherKey} ${r.firstName} ${r.lastName} ${r.email} ${r.uid ?? ""} ${r.staffRole ?? ""} ${(r.gradeLevels || []).join(" ")} ${(r.gradeLevels || []).map((x) => gradeLabelByValue[x] ?? "").join(" ")} ${(r.specialAssignments || []).join(" ")} ${(r.specialAssignments || []).map((x) => specialLabelByValue[x] ?? "").join(" ")}`.toLowerCase();
      return hay.includes(f);
    });
  }, [rows, filter, showInactive, gradeLabelByValue, specialLabelByValue]);

  async function load() {
    setLoading(true);
    setErr(null);
    try {
      const [optionsSnap, teachersSnap] = await Promise.all([
        getDoc(doc(db, "settings", "rosterOptions")),
        getDocs(query(collection(db, "teachers"), orderBy("lastName", "asc"))),
      ]);

      if (optionsSnap.exists()) {
        const d = optionsSnap.data() as any;
        setRosterOptions({
          gradeLevels: readRosterItems(d?.gradeLevels, DEFAULT_ROSTER_OPTIONS.gradeLevels),
          specialPrograms: ensurePrincipalSpecialProgram(readRosterItems(d?.specialPrograms, DEFAULT_ROSTER_OPTIONS.specialPrograms)),
        });
      } else {
        setRosterOptions(DEFAULT_ROSTER_OPTIONS);
      }

      const out: Row[] = teachersSnap.docs.map((d) => {
        const data = d.data() as any;
        return {
          id: d.id,
          ...data,
          staffRole: normalizeStaffRole(data.staffRole),
          gradeLevels: asStringArray(data.gradeLevels),
          specialAssignments: asStringArray(data.specialAssignments),
        };
      });
      setRows(out);
    } catch (e: any) {
      setErr(e?.message || "Failed to load teachers.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  async function inviteAndLinkStaff(emailValue: string, teacherDocId: string, role: StaffInviteRole) {
    if (role === "teacher") {
      const fn = httpsCallable(functions, "createTeacherInvite");
      await fn({ email: emailValue, sendEmailNow: true });
    } else {
      const fn = httpsCallable(functions, "createStaffInvite");
      await fn({ email: emailValue, role, sendEmailNow: true });
    }

    const linkFn = httpsCallable(functions, "linkTeacherAccount");
    await linkFn({ teacherDocId, email: emailValue, role });
  }

  async function addTeacher() {
    setErr(null);
    setSaving(true);
    try {
      const tk = norm(teacherKey);
      const emailNorm = normEmail(email);

      const docData: TeacherDoc = {
        teacherKey: tk,
        firstName: norm(firstName),
        lastName: norm(lastName),
        email: emailNorm,
        isActive: true,
        staffRole: inviteRole,
        gradeLevels,
        specialAssignments,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };

      if (!docData.teacherKey || !docData.firstName || !docData.lastName || !docData.email) {
        throw new Error("teacherKey, firstName, lastName, and email are required.");
      }

      await setDoc(doc(db, "teachers", docData.teacherKey), docData, { merge: true });

      if (sendInviteNow) {
        await inviteAndLinkStaff(docData.email, docData.teacherKey, inviteRole);
      }

      setTeacherKey("");
      setFirstName("");
      setLastName("");
      setEmail("");
      setInviteRole("teacher");
      setGradeLevels([]);
      setSpecialAssignments([]);

      await load();
      alert(sendInviteNow ? "Staff member added, invited, and linked." : "Staff member added.");
    } catch (e: any) {
      setErr(e?.message || "Failed to add teacher/staff.");
    } finally {
      setSaving(false);
    }
  }

  async function toggleActive(row: Row) {
    setErr(null);
    try {
      await updateDoc(doc(db, "teachers", row.id), {
        isActive: !row.isActive,
        updatedAt: serverTimestamp(),
      });
      await load();
    } catch (e: any) {
      setErr(e?.message || "Failed to update teacher.");
    }
  }

  async function resendInvite(row: Row) {
    setErr(null);
    setBusyRowId(row.id);
    try {
      if (!row.email) throw new Error("Teacher record is missing email.");
      const role = normalizeStaffRole(row.staffRole);
      await inviteAndLinkStaff(normEmail(row.email), row.id, role);
      await load();
      alert(`Invite resent to ${row.email}.`);
    } catch (e: any) {
      setErr(e?.message || "Failed to resend invite.");
    } finally {
      setBusyRowId(null);
    }
  }

  async function manualLink(row: Row) {
    setErr(null);
    setBusyRowId(row.id);
    try {
      if (!row.email) throw new Error("Teacher record is missing email.");
      const role = normalizeStaffRole(row.staffRole);
      const fn = httpsCallable(functions, "linkTeacherAccount");
      await fn({ teacherDocId: row.id, email: normEmail(row.email), role });
      await load();
      alert(`Linked ${row.email} to teacher ${row.teacherKey}.`);
    } catch (e: any) {
      setErr(e?.message || "Failed to link teacher account.");
    } finally {
      setBusyRowId(null);
    }
  }

  function beginEditAssignments(row: Row) {
    setEditingRowId(row.id);
    setEditingGrades(row.gradeLevels || []);
    setEditingSpecials(row.specialAssignments || []);
  }

  async function saveAssignments(row: Row) {
    setErr(null);
    setBusyRowId(row.id);
    try {
      await updateDoc(doc(db, "teachers", row.id), {
        gradeLevels: editingGrades,
        specialAssignments: editingSpecials,
        updatedAt: serverTimestamp(),
      });
      setEditingRowId(null);
      setEditingGrades([]);
      setEditingSpecials([]);
      await load();
    } catch (e: any) {
      setErr(e?.message || "Failed to update teacher assignments.");
    } finally {
      setBusyRowId(null);
    }
  }

  function displayGrade(value: string) {
    return gradeLabelByValue[value] || value;
  }

  function displaySpecial(value: string) {
    return specialLabelByValue[value] || value;
  }

  return (
    <div className="card">
      <h2>Roster — Teachers / Staff</h2>
      <p className="muted">Create and manage teachers and staff in Firestore.</p>

      {err ? <div className="error">{err}</div> : null}

      <div className="grid2">
        <div>
          <label className="label">Search</label>
          <input
            className="input"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Smith, T2003, teacher@zionclassical.org…"
          />
        </div>

        <div style={{ display: "flex", alignItems: "end" }}>
          <label style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <input
              type="checkbox"
              checked={showInactive}
              onChange={(e) => setShowInactive(e.target.checked)}
            />
            <span>Show inactive</span>
          </label>
        </div>
      </div>

      <hr className="hr" />

      <h3>Add Teacher / Staff</h3>
      <div className="grid2">
        <div>
          <label className="label">Teacher Key</label>
          <input className="input" value={teacherKey} onChange={(e) => setTeacherKey(e.target.value)} />
        </div>
        <div>
          <label className="label">Email</label>
          <input className="input" value={email} onChange={(e) => setEmail(e.target.value)} />
        </div>
        <div>
          <label className="label">First Name</label>
          <input className="input" value={firstName} onChange={(e) => setFirstName(e.target.value)} />
        </div>
        <div>
          <label className="label">Last Name</label>
          <input className="input" value={lastName} onChange={(e) => setLastName(e.target.value)} />
        </div>
        <div>
          <label className="label">Role</label>
          <select className="input" value={inviteRole} onChange={(e) => setInviteRole(e.target.value as StaffInviteRole)}>
            <option value="teacher">Teacher</option>
            <option value="office">Office</option>
            <option value="admin">Admin</option>
          </select>
        </div>
      </div>

      <div style={{ display: "grid", gap: 12, marginTop: 12 }}>
        <div>
          <label className="label">Assigned Grades</label>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
            {rosterOptions.gradeLevels.map((grade) => (
              <label key={grade.value} style={{ display: "flex", gap: 6, alignItems: "center" }}>
                <input
                  type="checkbox"
                  checked={gradeLevels.includes(grade.value)}
                  onChange={() => setGradeLevels((prev) => toggleListValue(prev, grade.value))}
                />
                <span>{grade.label}</span>
              </label>
            ))}
          </div>
          <div className="muted" style={{ marginTop: 6 }}>Homeroom and regular grade access.</div>
        </div>

        <div>
          <label className="label">Special Programs</label>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
            {rosterOptions.specialPrograms.length === 0 ? (
              <div className="muted">No special programs configured yet. Add them in Settings.</div>
            ) : (
              rosterOptions.specialPrograms.map((program) => (
                <label key={program.value} style={{ display: "flex", gap: 6, alignItems: "center" }}>
                  <input
                    type="checkbox"
                    checked={specialAssignments.includes(program.value)}
                    onChange={() => setSpecialAssignments((prev) => toggleListValue(prev, program.value))}
                  />
                  <span>{program.label}</span>
                </label>
              ))
            )}
          </div>
          <div className="muted" style={{ marginTop: 6 }}>Use this for Art, Music, Band, PE, and similar specials.</div>
        </div>
      </div>

      <label style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 12 }}>
        <input type="checkbox" checked={sendInviteNow} onChange={(e) => setSendInviteNow(e.target.checked)} />
        <span>Send account setup email now</span>
      </label>

      <button className="btn" disabled={saving} onClick={addTeacher} style={{ marginTop: 10 }}>
        {saving ? "Saving…" : "Add Teacher / Staff"}
      </button>

      <hr className="hr" />

      <h3>Teachers / Staff</h3>

      {loading ? (
        <div className="muted">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="muted">No teachers found.</div>
      ) : (
        <div className="table">
          <div className="thead" style={{ gridTemplateColumns: "120px 1fr 140px 220px 240px 110px 280px" }}>
            <div>Key</div>
            <div>Name</div>
            <div>Role</div>
            <div>Assignments</div>
            <div>Email / Link</div>
            <div>Status</div>
            <div></div>
          </div>

          {filtered.map((r) => {
            const rowBusy = busyRowId === r.id;
            const role = normalizeStaffRole(r.staffRole);
            const isEditing = editingRowId === r.id;

            return (
              <div key={r.id} style={{ display: "grid", gap: 8 }}>
                <div className="trow" style={{ gridTemplateColumns: "120px 1fr 140px 220px 240px 110px 280px" }}>
                  <div>{r.teacherKey}</div>
                  <div>{fullName(r)}</div>
                  <div><span className="pill ok">{role}</span></div>
                  <div>
                    <div>{(r.gradeLevels || []).map(displayGrade).join(", ") || "—"}</div>
                    <div className="muted" style={{ fontSize: 12 }}>{(r.specialAssignments || []).map(displaySpecial).join(", ") || "No specials"}</div>
                  </div>
                  <div>
                    <div>{r.email}</div>
                    <div className="muted" style={{ fontSize: 12 }}>
                      {r.uid ? `Linked • ${r.uid}` : "Not linked"}
                    </div>
                  </div>
                  <div>
                    <span className={r.isActive ? "pill ok" : "pill off"}>{r.isActive ? "Active" : "Inactive"}</span>
                  </div>
                  <div style={{ textAlign: "right", display: "flex", gap: 8, justifyContent: "flex-end", flexWrap: "wrap" }}>
                    <button className="btn small secondary" disabled={rowBusy} onClick={() => beginEditAssignments(r)}>Assignments</button>
                    <button className="btn small secondary" disabled={rowBusy} onClick={() => resendInvite(r)}>
                      {rowBusy ? "Working…" : "Invite"}
                    </button>
                    <button className="btn small secondary" disabled={rowBusy} onClick={() => manualLink(r)}>Link</button>
                    <button className="btn small secondary" disabled={rowBusy} onClick={() => toggleActive(r)}>
                      {r.isActive ? "Deactivate" : "Activate"}
                    </button>
                  </div>
                </div>
                {isEditing ? (
                  <div className="card" style={{ padding: 12, background: "rgba(0,0,0,0.02)" }}>
                    <div style={{ fontWeight: 700, marginBottom: 10 }}>Edit assignments for {fullName(r)}</div>
                    <div style={{ display: "grid", gap: 12 }}>
                      <div>
                        <label className="label">Assigned Grades</label>
                        <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                          {rosterOptions.gradeLevels.map((grade) => (
                            <label key={grade.value} style={{ display: "flex", gap: 6, alignItems: "center" }}>
                              <input type="checkbox" checked={editingGrades.includes(grade.value)} onChange={() => setEditingGrades((prev) => toggleListValue(prev, grade.value))} />
                              <span>{grade.label}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                      <div>
                        <label className="label">Special Programs</label>
                        <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                          {rosterOptions.specialPrograms.map((program) => (
                            <label key={program.value} style={{ display: "flex", gap: 6, alignItems: "center" }}>
                              <input type="checkbox" checked={editingSpecials.includes(program.value)} onChange={() => setEditingSpecials((prev) => toggleListValue(prev, program.value))} />
                              <span>{program.label}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                        <button className="btn secondary" onClick={() => setEditingRowId(null)}>Cancel</button>
                        <button className="btn" disabled={rowBusy} onClick={() => saveAssignments(r)}>{rowBusy ? "Saving…" : "Save Assignments"}</button>
                      </div>
                    </div>
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
